import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { IClientParam, IReportState, IThunkAPI } from '@/store/types';
import type {
  IGetFiltersPara,
  IGetReportMetadata,
  IGetTablePara,
  IReport,
  IReportFilter,
  IReportMetadata,
  ITableResult,
  IExportClassUsersParam,
} from '@/api/types';

import { report as api } from '@/api';
import { isKnownError, logError } from '@/helpers';

const initialState: IReportState = {
  reports: [],
  filters: [],
  tableResult: { loading: false, tableHeaders: [], configuration: [], tableRows: [] },
  clientParams: [],
  filtersRetrieving: false,
};

export const getReports = createAsyncThunk<{ list: IReport[] }, number, IThunkAPI>(
  'report/get/reports',
  async (clientID, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('getReports cancelled');
      });
      const response = await api.getReports(clientID, { cancelToken: source.token });
      if (isKnownError(response.data)) return { list: [] };

      return {
        list: response.data,
      };
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getReportMetadata = createAsyncThunk<IReportMetadata, IGetReportMetadata, IThunkAPI>(
  'report/get/metadata',
  async (payload, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('getReportMetadata cancelled');
      });
      const response = await api.getReportMetadata(payload, { cancelToken: source.token });
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getReportFilters = createAsyncThunk<IReportFilter[], IGetFiltersPara, IThunkAPI>(
  'report/get/filters',
  async (payload, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('getReportFilter cancelled');
      });
      const response = await api.getReportFilter(payload.id, payload.pairs, { cancelToken: source.token });
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getTableResult = createAsyncThunk<ITableResult, IGetTablePara, IThunkAPI>(
  'report/get/tableresults',
  async (payload, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('getReportFilter cancelled');
      });
      const response = await api.getTableResult(payload.id, payload.paras, payload.csv, { cancelToken: source.token });
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const getExportClassUsers = createAsyncThunk<string, IExportClassUsersParam, IThunkAPI>(
  'report/get/classUsersRemoved',
  async (payload, thunkAPI) => {
    try {
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => {
        source.cancel('getReportFilter cancelled');
      });
      const response = await api.getExportClassUsersResult(payload, { cancelToken: source.token });
      return response.data;
    } catch (e) {
      logError(e);
      return thunkAPI.rejectWithValue(e.response);
    }
  },
);

export const slice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    setReportParameter(state, action: PayloadAction<IClientParam>) {
      const params = state.clientParams;
      const param = action.payload;
      const idx = params.findIndex(p => p.filterKey === param.filterKey);
      if (idx === -1) {
        params.push(action.payload);
        return;
      }
      state.clientParams[idx].filter = action.payload.filter;
      state.clientParams[idx].isMulti = action.payload.isMulti;
      state.clientParams[idx].value = action.payload.value;
    },
    clearReportParameter(state) {
      state.clientParams = [];
      state.filters = [];
      state.tableResult.tableRows = [];
      state.tableResult.tableHeaders = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getReports.fulfilled, (state, action) => {
        state.reports = action.payload.list;
      })
      .addCase(getReportMetadata.fulfilled, (state, action) => {
        state.metadata = action.payload;
      })
      .addCase(getReportFilters.pending, state => {
        state.filtersRetrieving = true;
      })
      .addCase(getReportFilters.fulfilled, (state, action) => {
        state.filters = action.payload;
        state.filtersRetrieving = false;
      })
      .addCase(getReportFilters.rejected, state => {
        state.filtersRetrieving = false;
      })
      .addCase(getTableResult.pending, state => {
        state.tableResult = { loading: true, tableHeaders: [], configuration: [], tableRows: [] };
      })
      .addCase(getTableResult.fulfilled, (state, action) => {
        const payload = action.payload;
        if (typeof payload === 'string') {
          state.tableResult = {
            loading: false,
            tableHeaders: [],
            configuration: [],
            tableRows: [],
            csvContent: payload,
          };
        } else {
          state.tableResult = payload;
          state.tableResult.loading = false;
        }
      })
      .addCase(getTableResult.rejected, state => {
        state.tableResult = {
          loading: false,
          tableHeaders: [],
          configuration: [],
          tableRows: [],
          error: 'Error occurred, please ensure all report filers are set with valid values.',
        };
      });
  },
});

// actions
export const { setReportParameter, clearReportParameter } = slice.actions;

export default slice.reducer;
